import type { QueryClient } from '@tanstack/react-query'

import { MassChannel, MassJobStatus } from '#/shared/api/generated/communication/models'
import type { MassCallResponse, MassJobSummary } from '#/shared/api/generated/communication/models'

import { activeMassJobsQueryKey } from './use-active-mass-jobs'

/*
 * The two job-cache helpers the vendored campaign pages import, copied from the
 * product's `shared/lib/mass-jobs/job-cache.ts`. The rest of that file serves the
 * product's mass-send pills, which the console does not render.
 */

type MassJobsResponse = { data: MassJobSummary[] }

function percent(done: number, total: number): number {
  return total > 0 ? Math.round((done / total) * 100) : 0
}

/** `POST /send/mass/call` answers with the session it just created, so the
 *  pill's row can be seeded from the response rather than from a refetch of
 *  the list the response already describes. */
export function massCallResponseToJobSummary(response: MassCallResponse): MassJobSummary {
  const done = response.called_count
  return {
    id: response.id,
    user_id: response.user_id,
    channel: MassChannel.call,
    status: MassJobStatus.processing,
    from_address: response.from_number,
    total: response.total_count,
    done,
    failed: response.failed_count,
    progress_percent: percent(done, response.total_count),
    started_at: response.started_at,
    completed_at: response.completed_at,
    created_at: response.created_at,
  }
}

/**
 * Insert or replace a row in the cached active-jobs list.
 *
 * Returns `false` when no list is cached at all — the caller decides what that
 * means (usually nothing: the list's own fetch is what will bring the row in).
 */
export function upsertMassJob(queryClient: QueryClient, job: MassJobSummary): boolean {
  const key = activeMassJobsQueryKey()
  const cached = queryClient.getQueryData<MassJobsResponse>(key)
  if (!cached) return false
  const index = cached.data.findIndex((existing) => existing.id === job.id)
  const data =
    index === -1
      ? [...cached.data, job]
      : cached.data.map((existing, at) => (at === index ? job : existing))
  queryClient.setQueryData<MassJobsResponse>(key, { ...cached, data })
  return true
}
